// GAME SOUNDS — a small procedural kit (Web Audio, no files): coin-pop clicks that climb with the combo, a row chime, a gold
// surplus ting, season stings, a harvest fanfare, the crow, the shoo, the glint, a coin pop, a kneel rustle.
//
// Every sound is a few layered oscillators / filtered-noise bursts with real envelopes, through one compressor so a burst of
// clicks never clips. Audition them on /sound-lab.html (dev). Nothing here touches the AudioContext until the first play
// (browsers gate audio behind a user gesture; the paw click IS one). `setLevelSource(fn)` lets the game tie the level to the
// music panel's mute / volume. In DEV, window.__sfx.stats() counts every request (the headless tests cannot hear).

let ctx = null, master = null, noiseBuf = null
let levelSource = () => 1
const stats = {}
const count = (name) => { stats[name] = (stats[name] ?? 0) + 1 }

export function setLevelSource(fn) { levelSource = typeof fn === 'function' ? fn : () => 1 }

function ensure() {
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext
    if (!AC) return null
    ctx = new AC()
    const comp = ctx.createDynamicsCompressor()
    comp.threshold.value = -18; comp.knee.value = 12; comp.ratio.value = 6; comp.attack.value = 0.003; comp.release.value = 0.12
    master = ctx.createGain()
    master.gain.value = 0.9
    master.connect(comp).connect(ctx.destination)
    const n = ctx.sampleRate // one second of white noise, reused by every noise burst
    noiseBuf = ctx.createBuffer(1, n, ctx.sampleRate)
    const d = noiseBuf.getChannelData(0)
    for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1
  }
  if (ctx.state === 'suspended') ctx.resume()
  return ctx
}

// ── voices ──────────────────────────────────────────────────────────────────────────────────────────────────────────
// A tone: `f0` → `f1` over `slide` seconds (exponential), attack / decay envelope, optional detune cents and vibrato.
function tone({ type = 'sine', f0 = 440, f1 = null, slide = 0.05, at = 0, dur = 0.15, gain = 0.2, attack = 0.004, detune = 0, vib = 0, vibHz = 6, lp = null }) {
  const c = ctx, t0 = c.currentTime + at, g = c.createGain(), o = c.createOscillator()
  o.type = type
  o.frequency.setValueAtTime(Math.max(20, f0), t0)
  if (f1) o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + slide)
  if (detune) o.detune.value = detune
  if (vib) { const l = c.createOscillator(), lg = c.createGain(); l.frequency.value = vibHz; lg.gain.value = vib; l.connect(lg).connect(o.frequency); l.start(t0); l.stop(t0 + dur + 0.05) }
  const lvl = gain * levelSource()
  g.gain.setValueAtTime(0.0001, t0)
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, lvl), t0 + attack)
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
  let out = g
  if (lp) { const f = c.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = lp; f.Q.value = 0.7; g.connect(f); out = f }
  o.connect(g); out.connect(master)
  o.start(t0); o.stop(t0 + dur + 0.05)
}
// A noise burst through a band-pass (or low-pass) filter whose centre sweeps `f0` → `f1`.
function noise({ at = 0, dur = 0.12, gain = 0.1, type = 'bandpass', f0 = 2000, f1 = null, q = 1, attack = 0.003 }) {
  const c = ctx, t0 = c.currentTime + at, s = c.createBufferSource(), g = c.createGain(), f = c.createBiquadFilter()
  s.buffer = noiseBuf; s.loop = true
  f.type = type; f.Q.value = q
  f.frequency.setValueAtTime(f0, t0)
  if (f1) f.frequency.exponentialRampToValueAtTime(f1, t0 + dur)
  const lvl = gain * levelSource()
  g.gain.setValueAtTime(0.0001, t0)
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, lvl), t0 + attack)
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
  s.connect(f).connect(g).connect(master)
  s.start(t0); s.stop(t0 + dur + 0.05)
}
const semi = (f, n) => f * Math.pow(2, n / 12)
const jitter = (cents = 12) => (Math.random() * 2 - 1) * cents
const guard = (name, fn) => { count(name); try { if (!ensure() || levelSource() <= 0) return; fn() } catch (err) { console.debug(`[sfx] ${name} skipped:`, err) } }

// ── the sounds ──────────────────────────────────────────────────────────────────────────────────────────────────────
// The paw: a coin-pop. `combo` 0…1 raises the pitch a fifth in semitone steps, so a steady streak audibly climbs.
export function click(combo = 0) {
  guard('click', () => {
    const base = semi(523, Math.round(Math.max(0, Math.min(1, combo)) * 7))
    tone({ type: 'triangle', f0: base, f1: base * 1.9, slide: 0.035, dur: 0.12, gain: 0.26, detune: jitter(10) })   // the pop
    tone({ type: 'square', f0: base * 3, dur: 0.025, gain: 0.05, attack: 0.001 })                                   // the tick
    tone({ type: 'sine', f0: 170, f1: 90, slide: 0.06, dur: 0.07, gain: 0.14 })                                    // the thump
  })
}
// A row filled: a quick rising arpeggio with a shimmer on top.
export function rowFilled() {
  guard('rowFilled', () => {
    const arp = [523.25, 659.25, 783.99, 1046.5]
    arp.forEach((f, i) => {
      tone({ type: 'triangle', f0: f, at: i * 0.055, dur: 0.24, gain: 0.16 })
      tone({ type: 'sine', f0: f * 2, at: i * 0.055, dur: 0.18, gain: 0.05 })
    })
    noise({ at: 0.16, dur: 0.14, gain: 0.05, f0: 6000, f1: 9000, q: 2 })
  })
}
// Clicking past a full field: a gold ting that nudges upward every few.
export function surplus(n = 0) {
  guard('surplus', () => {
    const f = semi(1568, (Math.floor(n / 5) % 4) * 2)
    tone({ type: 'sine', f0: f, dur: 0.28, gain: 0.12, vib: 6, vibHz: 7 })
    tone({ type: 'sine', f0: f * 1.5, dur: 0.22, gain: 0.07, at: 0.01 })
    tone({ type: 'triangle', f0: f / 2, dur: 0.09, gain: 0.08 })
  })
}
// The combo reaches its top: a two-note ding. The streak breaks: a soft falling wah.
export function comboMax() {
  guard('comboMax', () => {
    tone({ type: 'square', f0: 1318.5, dur: 0.09, gain: 0.07 })
    tone({ type: 'triangle', f0: 1760, at: 0.09, dur: 0.3, gain: 0.14 })
    tone({ type: 'sine', f0: 3520, at: 0.09, dur: 0.2, gain: 0.04 })
  })
}
export function comboBreak() {
  guard('comboBreak', () => {
    tone({ type: 'sine', f0: 660, f1: 240, slide: 0.22, dur: 0.26, gain: 0.11, lp: 1800 })
    tone({ type: 'triangle', f0: 330, f1: 120, slide: 0.22, dur: 0.24, gain: 0.05, lp: 900 })
  })
}
// The season turns: a different sting for each — PREP a whoosh and a tom, PLANT a water drop, MAINTAIN two warm notes,
// HARVEST a bright chord.
export function seasonTurn(season = 'PREP') {
  guard('seasonTurn', () => {
    switch (String(season).toUpperCase()) {
      case 'PLANT':
        noise({ dur: 0.3, gain: 0.08, type: 'lowpass', f0: 400, f1: 2400, q: 0.8 })
        tone({ type: 'sine', f0: 880, f1: 1500, slide: 0.08, at: 0.12, dur: 0.16, gain: 0.16 })
        tone({ type: 'sine', f0: 1320, f1: 1980, slide: 0.07, at: 0.26, dur: 0.14, gain: 0.1 })
        break
      case 'MAINTAIN':
        tone({ type: 'triangle', f0: 659.25, dur: 0.28, gain: 0.15 })
        tone({ type: 'triangle', f0: 783.99, at: 0.14, dur: 0.36, gain: 0.15 })
        tone({ type: 'sine', f0: 1567.98, at: 0.14, dur: 0.3, gain: 0.05 })
        break
      case 'HARVEST': {
        const chord = [523.25, 659.25, 783.99, 1046.5]
        chord.forEach((f, i) => tone({ type: 'sawtooth', f0: f, at: i * 0.04, dur: 0.6, gain: 0.07, lp: 2600, detune: jitter(6) }))
        noise({ at: 0.1, dur: 0.4, gain: 0.05, f0: 5000, f1: 9000, q: 1.5 })
        break
      }
      default: // PREP
        noise({ dur: 0.36, gain: 0.12, type: 'lowpass', f0: 300, f1: 1600, q: 0.9 })
        tone({ type: 'sine', f0: 220, f1: 95, slide: 0.2, dur: 0.28, gain: 0.22 })
        tone({ type: 'triangle', f0: 330, f1: 140, slide: 0.18, at: 0.02, dur: 0.2, gain: 0.06 })
    }
  })
}
// The harvest payout: a fanfare — four rising notes, a held chord, sparkle.
export function harvest() {
  guard('harvest', () => {
    const notes = [523.25, 659.25, 783.99, 1046.5]
    notes.forEach((f, i) => tone({ type: 'square', f0: f, at: i * 0.09, dur: 0.16, gain: 0.07, lp: 3000 }))
    notes.forEach((f) => { tone({ type: 'sawtooth', f0: f, at: 0.38, dur: 0.8, gain: 0.06, lp: 2400, detune: jitter(8) }); tone({ type: 'sine', f0: f * 2, at: 0.38, dur: 0.6, gain: 0.03 }) })
    noise({ at: 0.4, dur: 0.5, gain: 0.06, f0: 6000, f1: 10000, q: 1.5 })
    tone({ type: 'sine', f0: 130, f1: 65, slide: 0.3, at: 0.38, dur: 0.5, gain: 0.16 })
  })
}
// The crow lands: two rasping caws. Shooing it: a feather burst and a falling chirp.
export function crowCaw() {
  guard('crowCaw', () => {
    for (let i = 0; i < 2; i++) {
      tone({ type: 'sawtooth', f0: 720, f1: 480, slide: 0.16, at: i * 0.24, dur: 0.19, gain: 0.1, vib: 40, vibHz: 28, lp: 1800 })
      noise({ at: i * 0.24, dur: 0.18, gain: 0.05, f0: 1200, f1: 800, q: 3 })
    }
  })
}
export function shoo() {
  guard('shoo', () => {
    noise({ dur: 0.22, gain: 0.12, f0: 3000, f1: 1500, q: 1.2 })
    noise({ at: 0.06, dur: 0.16, gain: 0.07, f0: 4500, f1: 2500, q: 1.5 })
    tone({ type: 'square', f0: 1400, f1: 500, slide: 0.18, at: 0.02, dur: 0.2, gain: 0.05, lp: 2200 })
  })
}
// The crow eats a notch: a crunch and a low, sour note.
export function crowEat() {
  guard('crowEat', () => {
    noise({ dur: 0.09, gain: 0.12, type: 'lowpass', f0: 1800, f1: 600, q: 0.8 })
    noise({ at: 0.1, dur: 0.08, gain: 0.1, type: 'lowpass', f0: 1400, f1: 500, q: 0.8 })
    tone({ type: 'triangle', f0: 196, f1: 150, slide: 0.25, at: 0.05, dur: 0.32, gain: 0.12, lp: 900 })
  })
}
// A golden glint appears / is caught: a sprinkle of high blips (a few more when caught).
export function glint(caught = false) {
  guard(caught ? 'glintCaught' : 'glint', () => {
    const n = caught ? 6 : 3
    for (let i = 0; i < n; i++) tone({ type: 'sine', f0: 2200 + Math.random() * 1500, at: i * 0.05, dur: 0.06, gain: 0.07 })
    if (caught) tone({ type: 'triangle', f0: 1046.5, f1: 2093, slide: 0.12, at: 0.1, dur: 0.3, gain: 0.12 })
  })
}
// A coin drops from a citizen's work.
export function coinPop() {
  guard('coinPop', () => {
    tone({ type: 'square', f0: 1046.5, f1: 1568, slide: 0.04, dur: 0.07, gain: 0.08 })
    tone({ type: 'sine', f0: 2093, at: 0.05, dur: 0.14, gain: 0.07 })
  })
}
// A kneel in the rice: a soft rustle.
export function kneel() {
  guard('kneel', () => { noise({ dur: 0.22, gain: 0.045, type: 'lowpass', f0: 900, f1: 500, q: 0.8 }); noise({ at: 0.08, dur: 0.16, gain: 0.03, f0: 2500, f1: 1800, q: 1 }) })
}

export const sounds = { click, rowFilled, surplus, comboMax, comboBreak, seasonTurn, harvest, crowCaw, crowEat, shoo, glint, coinPop, kneel }

// A built-in sound by its id — 'name' or 'name:arg' ('seasonTurn:PREP', 'glint:caught'); the event's own arguments follow.
export function playById(id, ...args) {
  const [name, arg] = String(id).split(':')
  const fn = sounds[name]; if (!fn) return false
  if (arg != null) fn(arg === 'caught' ? true : arg)
  else fn(...args)
  return true
}
// An uploaded sound file (Testing Field → Sounds): decoded once, cached, played through the same master and compressor.
const fileCache = new Map() // name → AudioBuffer | Promise
export function playFile(name, getBlob) {
  count(`file:${name}`)
  try {
    if (!ensure() || levelSource() <= 0) return
    let buf = fileCache.get(name)
    if (!buf) { buf = getBlob(name).then((blob) => blob ? blob.arrayBuffer() : null).then((ab) => (ab ? ctx.decodeAudioData(ab) : null)).then((b) => { fileCache.set(name, b); return b }); fileCache.set(name, buf) }
    Promise.resolve(buf).then((b) => {
      if (!b) return
      const src = ctx.createBufferSource(), g = ctx.createGain()
      src.buffer = b; g.gain.value = 0.9 * levelSource()
      src.connect(g).connect(master); src.start()
    }).catch((err) => console.debug('[sfx] file skipped:', err))
  } catch (err) { console.debug('[sfx] file skipped:', err) }
}
export function forgetFile(name) { fileCache.delete(name) }
export const sfxStats = () => ({ ...stats })
if (typeof window !== 'undefined' && import.meta.env?.DEV) window.__sfx = { stats: sfxStats, play: (name, ...a) => sounds[name]?.(...a), reset: () => { for (const k of Object.keys(stats)) delete stats[k] } }
